import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useLoading } from '@/context/LoadingContext';

interface UseOperationOptions<T> {
  operation: () => Promise<T>;
  loadingMessage?: string;
  successMessage?: string | ((result: T) => string);
  errorMessage?: string | ((error: Error) => string);
  onSuccess?: (result: T) => void;
  onError?: (error: Error) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

interface UseOperationReturn<T> {
  execute: () => Promise<T | undefined>;
  isLoading: boolean;
  error: Error | null;
  data: T | undefined;
}

export function useOperation<T = void>({
  operation,
  loadingMessage = 'Processing...',
  successMessage,
  errorMessage,
  onSuccess,
  onError,
  showSuccessToast = true,
  showErrorToast = true,
}: UseOperationOptions<T>): UseOperationReturn<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | undefined>(undefined);
  const { toast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const execute = useCallback(async (): Promise<T | undefined> => {
    if (isLoading) {
      console.warn('Operation already in progress');
      return undefined;
    }

    const operationId = `operation-${Date.now()}-${Math.random()}`;
    setIsLoading(true);
    setError(null);
    startLoading(operationId, loadingMessage);

    let toastDismiss: (() => void) | undefined;
    if (loadingMessage) {
      const result = toast({
        title: loadingMessage,
        duration: Infinity,
      });
      toastDismiss = result.dismiss;
    }

    try {
      const result = await operation();
      setData(result);

      // Dismiss loading toast
      if (toastDismiss) {
        toastDismiss();
      }

      if (showSuccessToast && successMessage) {
        const message = typeof successMessage === 'function' 
          ? successMessage(result) 
          : successMessage;

        toast({
          title: 'Success',
          description: message,
          variant: 'default',
        });
      }

      onSuccess?.(result);
      return result;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      setError(errorObj);

      // Dismiss loading toast
      if (toastDismiss) {
        toastDismiss();
      }

      if (showErrorToast) {
        const message = errorMessage
          ? typeof errorMessage === 'function'
            ? errorMessage(errorObj)
            : errorMessage
          : errorObj.message;

        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
      }

      onError?.(errorObj);
      throw errorObj;
    } finally {
      setIsLoading(false);
      stopLoading(operationId);
    }
  }, [
    isLoading,
    operation,
    loadingMessage,
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    showSuccessToast,
    showErrorToast,
    toast,
    startLoading,
    stopLoading,
  ]);

  return {
    execute,
    isLoading,
    error,
    data,
  };
}
